import { Box, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'

/**
 * ImportProcessingIndicator
 * -------------------------------------------------------------------------
 * A self-contained "processing" card shown while a file import is running.
 * Recreated from the Claude Design handoff ("Import Processing"): a spinner
 * header, animated progress bar with a live percentage / row counter, a step
 * pipeline (Upload → Validate → Parse → Import → Finalise) derived from the
 * percentage, rotating reassurance messages, and a success state.
 *
 * Values are taken from the handoff's design-system tokens (`ds/tokens.css`):
 * spacing on the 8px rhythm, the MUI-rooted type scale, and the grey ramp for
 * neutral chrome. The green accent reads from `theme.palette.primary.*` (and
 * `alpha()` for the faint tint) so partner branding flows through, per the
 * frontend styling guide.
 *
 * Drive it from real progress data — pass `percent` (and optionally
 * `rowsDone` / `rowsTotal`), flip `complete` when the backend reports done,
 * or set `indeterminate` while a total is still unknown (e.g. queued).
 * `scale` and `maxWidth` size the card up for more prominent placements while
 * keeping every proportion intact (defaults render the exact design size).
 */

interface ImportStepDef {
  /** Chip label. */
  short: string
  /** Longer status-line label shown above the bar while this step is active. */
  long: string
  /** Upper percentage bound — the step is "done" once `percent` reaches it. */
  end: number
}

const DEFAULT_STEPS: ImportStepDef[] = [
  { short: 'Upload', long: 'Uploading file', end: 8 },
  { short: 'Validate', long: 'Validating data', end: 28 },
  { short: 'Parse', long: 'Parsing rows', end: 58 },
  { short: 'Import', long: 'Importing records', end: 90 },
  { short: 'Finalise', long: 'Finalising', end: 100 },
]

const DEFAULT_MESSAGES = [
  'Validating rows…',
  'Checking for duplicate entries…',
  'Matching emission factors…',
  'Crunching the numbers…',
  'Almost there…',
]

// The final reassurance line (e.g. "Almost there…") is held out of the rotation until progress
// passes this percentage, so it never shows early.
const NEAR_DONE_THRESHOLD = 80

const numberFormatter = new Intl.NumberFormat('en-GB')

// Neutral chrome resolved from the DS grey ramp (ds/colors.css).
const GREY_800 = '#34403f' // status label
const GREY_700 = '#4a5658' // rotating message
const GREY_600 = '#667072' // --text-secondary
const GREY_500 = '#889092' // eta / footer
const GREY_400 = '#b4babb' // step dot (pending)
const GREY_300 = '#d6dadb' // --border-default (card border)
const GREY_200 = '#e6e9e9' // --border-subtle (chip border, hairline divider)
const GREY_100 = '#f1f3f2' // --surface-sunken (bar track, pending chip bg)

export interface ImportProcessingIndicatorProps {
  /** Current progress, 0–100. Ignored while `indeterminate`. */
  percent?: number
  /** Rows processed so far. Defaults to `round(percent% × rowsTotal)`. */
  rowsDone?: number
  /** Total rows. When omitted, the rows line is hidden. */
  rowsTotal?: number
  /** Force the success state regardless of `percent`. */
  complete?: boolean
  /** No known total/percent yet (e.g. queued) — shows a sliding bar. */
  indeterminate?: boolean
  /** Header title (in-progress state). */
  title?: string
  /** Header subtitle (in-progress state). */
  subtitle?: string
  /** Title shown in the success state. */
  completeTitle?: string
  /** Subtitle shown in the success state. Overrides the default rows summary. */
  completeSubtitle?: string
  /** Optional hint shown to the right of the row counter (e.g. an ETA). */
  etaText?: string
  /** Rotating reassurance messages cycled beneath the step pipeline. */
  messages?: string[]
  /**
   * Override the step pipeline. Pass plain label strings (thresholds spaced evenly) or
   * `{ short, long?, end? }` objects when you want a compact chip label distinct from the
   * status-line copy and/or explicit percentage thresholds.
   */
  steps?: Array<string | { short: string; long?: string; end?: number }>
  /** Reassurance footer copy (in-progress state). */
  footerText?: string
  /** Hide the reassurance footer entirely. */
  hideFooter?: boolean
  /** Noun used in the rows line and success summary. Defaults to "rows". */
  unitLabel?: string
  /** Uniform size multiplier for every dimension. `1` = exact design size. */
  scale?: number
  /** Max card width in px. */
  maxWidth?: number
}

export const ImportProcessingIndicator: FunctionComponent<ImportProcessingIndicatorProps> = ({
  percent = 0,
  rowsDone,
  rowsTotal,
  complete = false,
  indeterminate = false,
  title = 'Processing import',
  subtitle = 'Bringing your data into the system',
  completeTitle = 'Import complete',
  completeSubtitle,
  etaText,
  messages = DEFAULT_MESSAGES,
  steps,
  footerText = 'Large files can take a moment. You can keep this tab open and carry on elsewhere.',
  hideFooter = false,
  unitLabel = 'rows',
  scale = 1,
  maxWidth = 480,
}) => {
  const theme = useTheme()
  const accent = theme.palette.primary.main
  const accentLight = theme.palette.primary.light
  const accentDark = theme.palette.primary.dark
  // --surface-tint / --green-50 (#f1f6e7), derived from the brand so it tracks
  // partner theming rather than pinning a fixed green.
  const tint = alpha(accent, 0.1)

  // Scale a px dimension; `sp()` scales an MUI spacing step (× 8px).
  const s = (n: number) => Math.round(n * scale)
  const sp = (n: number) => n * scale

  const clampedPercent = Math.max(0, Math.min(100, percent))
  const showComplete = complete || (!indeterminate && clampedPercent >= 100)

  // Build the step pipeline — either the rich defaults or evenly-spaced
  // thresholds from caller-supplied labels.
  const stepDefs = useMemo<ImportStepDef[]>(() => {
    if (!steps || steps.length === 0) return DEFAULT_STEPS
    return steps.map((entry, i) => {
      const evenEnd = Math.round(((i + 1) / steps.length) * 100)
      if (typeof entry === 'string') return { short: entry, long: entry, end: evenEnd }
      return { short: entry.short, long: entry.long ?? entry.short, end: entry.end ?? evenEnd }
    })
  }, [steps])

  // Rotate the reassurance message while the import is in progress. The final line is held back
  // until progress passes NEAR_DONE_THRESHOLD so an "Almost there…"-style message never shows early.
  const activeMessages = useMemo(() => (
    messages.length > 1 && !showComplete && clampedPercent < NEAR_DONE_THRESHOLD
      ? messages.slice(0, -1)
      : messages
  ), [messages, showComplete, clampedPercent])
  const [msgIndex, setMsgIndex] = useState(0)
  useEffect(() => {
    if (showComplete || activeMessages.length === 0) return
    const id = setInterval(() => {
      setMsgIndex((i) => (i + 1) % activeMessages.length)
    }, 2400)
    return () => clearInterval(id)
  }, [showComplete, activeMessages.length])

  // Active step = first step not yet finished (falls back to the last one).
  let activeIdx = stepDefs.findIndex((step) => clampedPercent < step.end)
  if (activeIdx === -1) activeIdx = stepDefs.length - 1

  const resolvedRowsDone = showComplete
    ? rowsTotal
    : rowsDone ?? (rowsTotal != null ? Math.round((clampedPercent / 100) * rowsTotal) : undefined)

  const statusLabel = indeterminate ? 'Preparing…' : stepDefs[activeIdx].long
  const rotatingMsg = activeMessages.length ? activeMessages[msgIndex % activeMessages.length] : ''

  const cardSx = {
    width: '100%',
    maxWidth,
    mx: 'auto',
    bgcolor: '#ffffff', // --surface-card
    border: `1px solid ${GREY_300}`, // --border-card
    borderRadius: `${s(14)}px`, // --radius-lg
    boxShadow: 'none', // flat card — border only
    p: `${s(32)}px ${s(32)}px ${s(24)}px`, // --space-8 --space-8 --space-6
    color: 'text.primary',
    fontFamily: theme.typography.fontFamily, // Effra — Box spans don't inherit theme typography, so set it on the root
    boxSizing: 'border-box',
    // Keyframes are emitted globally by Emotion under these literal names,
    // so descendant elements can reference them by name.
    '@keyframes ip-spin': { to: { transform: 'rotate(360deg)' } },
    '@keyframes ip-shimmer': {
      '0%': { transform: 'translateX(-120%)' },
      '100%': { transform: 'translateX(220%)' },
    },
    '@keyframes ip-pulse': {
      '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(accent, 0.35)}` },
      '50%': { boxShadow: `0 0 0 ${s(5)}px ${alpha(accent, 0)}` },
    },
    '@keyframes ip-dot': {
      '0%, 100%': { opacity: 0.35, transform: 'scale(0.85)' },
      '50%': { opacity: 1, transform: 'scale(1)' },
    },
    '@keyframes ip-pop': {
      '0%': { transform: 'scale(0.6)', opacity: 0 },
      '60%': { transform: 'scale(1.08)' },
      '100%': { transform: 'scale(1)', opacity: 1 },
    },
    '@keyframes ip-msg': {
      '0%': { opacity: 0, transform: 'translateY(4px)' },
      '12%': { opacity: 1, transform: 'translateY(0)' },
      '88%': { opacity: 1, transform: 'translateY(0)' },
      '100%': { opacity: 0, transform: 'translateY(-4px)' },
    },
    '@keyframes ip-indeterminate': {
      '0%': { left: '-45%' },
      '100%': { left: '100%' },
    },
  } as const

  if (showComplete) {
    const completeSummary =
      completeSubtitle ??
      (rowsTotal != null
        ? `${numberFormatter.format(rowsTotal)} ${unitLabel} imported successfully`
        : `All ${unitLabel} imported successfully`)
    return (
      <Box sx={cardSx}>
        <Box sx={{ textAlign: 'center', p: `${s(8)}px ${s(4)}px ${s(6)}px` }}>
          <Box
            sx={{
              width: s(52),
              height: s(52),
              mx: 'auto',
              borderRadius: '50%',
              bgcolor: tint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'ip-pop 0.4s ease-out',
            }}
          >
            <svg width={s(26)} height={s(26)} viewBox='0 0 24 24' fill='none'>
              <path
                d='M5 12.5L10 17.5L19 7'
                stroke={accentDark}
                strokeWidth='2.6'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </Box>
          <Typography
            sx={{ fontSize: s(18), fontWeight: 600, mt: sp(2), letterSpacing: '-0.01em' }}
          >
            {completeTitle}
          </Typography>
          <Typography sx={{ fontSize: s(13), color: GREY_600, mt: sp(0.5) }}>
            {completeSubtitle == null && rowsTotal != null ? (
              <>
                <Box component='span' sx={{ fontVariantNumeric: 'tabular-nums', color: accentDark }}>
                  {numberFormatter.format(rowsTotal)}
                </Box>
                {` ${unitLabel} imported successfully`}
              </>
            ) : (
              completeSummary
            )}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={cardSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: `${s(13)}px` }}>
        <Box sx={{ position: 'relative', width: s(38), height: s(38), flex: 'none' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `3px solid ${tint}`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '3px solid transparent',
              borderTopColor: accent,
              borderRightColor: accent,
              animation: 'ip-spin 0.9s linear infinite',
            }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: s(16), fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: s(13), color: GREY_600, lineHeight: 1.35, mt: '1px' }}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: sp(0.5),
            p: `${s(5)}px ${s(11)}px`,
            bgcolor: tint,
            borderRadius: '999px',
            flex: 'none',
          }}
        >
          <Box
            component='span'
            sx={{
              width: s(6),
              height: s(6),
              borderRadius: '50%',
              bgcolor: accent,
              animation: 'ip-dot 1.4s ease-in-out infinite',
            }}
          />
          <Box
            component='span'
            sx={{
              fontSize: s(11),
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: accentDark,
            }}
          >
            Working
          </Box>
        </Box>
      </Box>

      {/* Progress */}
      <Box sx={{ mt: sp(3) }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Box component='span' sx={{ fontSize: s(14), fontWeight: 500, color: GREY_800 }}>
            {statusLabel}
          </Box>
          {!indeterminate && (
            <Box
              component='span'
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: s(22),
                fontWeight: 500,
                color: 'text.primary',
                letterSpacing: '-0.02em',
              }}
            >
              {Math.round(clampedPercent)}%
            </Box>
          )}
        </Box>
        <Box
          sx={{
            position: 'relative',
            mt: `${s(9)}px`,
            height: s(9),
            borderRadius: '999px',
            bgcolor: GREY_100,
            overflow: 'hidden',
          }}
        >
          {indeterminate ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                height: '100%',
                width: '40%',
                borderRadius: '999px',
                background: `linear-gradient(90deg, ${accentLight}, ${accent})`,
                animation: 'ip-indeterminate 1.3s ease-in-out infinite',
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                borderRadius: '999px',
                background: `linear-gradient(90deg, ${accentLight}, ${accent})`,
                width: `${clampedPercent}%`,
                transition: 'width 0.4s ease',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '55%',
                  background:
                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                  animation: 'ip-shimmer 1.5s ease-in-out infinite',
                }}
              />
            </Box>
          )}
        </Box>
        {(resolvedRowsDone != null || etaText) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mt: sp(1),
            }}
          >
            <Box component='span' sx={{ fontVariantNumeric: 'tabular-nums', fontSize: s(12), color: GREY_600 }}>
              {resolvedRowsDone != null && rowsTotal != null
                ? `${numberFormatter.format(resolvedRowsDone)} / ${numberFormatter.format(rowsTotal)} ${unitLabel}`
                : resolvedRowsDone != null
                  ? `${numberFormatter.format(resolvedRowsDone)} ${unitLabel}`
                  : ''}
            </Box>
            {etaText && (
              <Box component='span' sx={{ fontSize: s(12), color: GREY_500 }}>
                {etaText}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Step pipeline */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${s(7)}px`, mt: sp(2.5) }}>
        {stepDefs.map((step, i) => {
          const isDone = clampedPercent >= step.end && !indeterminate
          const isActive = !isDone && i === activeIdx
          let bg = GREY_100
          let border = GREY_200
          let fg = GREY_500
          let dot = GREY_400
          let weight = 500
          let anim = 'none'
          if (isDone) {
            bg = accent
            border = accent
            fg = '#ffffff'
            weight = 600
          } else if (isActive) {
            bg = '#ffffff'
            border = accent
            fg = accentDark
            dot = accent
            weight = 600
            anim = 'ip-pulse 1.7s ease-in-out infinite'
          }
          return (
            <Box
              key={step.short + i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: sp(0.5),
                p: `${s(6)}px ${s(11)}px`,
                borderRadius: '999px',
                bgcolor: bg,
                border: `1px solid ${border}`,
                animation: anim,
              }}
            >
              {isDone ? (
                <svg width={s(11)} height={s(11)} viewBox='0 0 12 12' fill='none' style={{ flex: 'none' }}>
                  <path
                    d='M2.5 6.2L4.8 8.5L9.5 3.5'
                    stroke='#ffffff'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              ) : (
                <Box
                  component='span'
                  sx={{ width: s(6), height: s(6), borderRadius: '50%', bgcolor: dot }}
                />
              )}
              <Box
                component='span'
                sx={{ fontSize: s(12), fontWeight: weight, color: fg, whiteSpace: 'nowrap' }}
              >
                {step.short}
              </Box>
            </Box>
          )
        })}
      </Box>

      {/* Rotating status */}
      {rotatingMsg && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: sp(1),
            mt: sp(2.5),
            pt: sp(2),
            borderTop: `1px solid ${GREY_200}`,
          }}
        >
          <Box
            component='span'
            sx={{
              width: s(6),
              height: s(6),
              borderRadius: '50%',
              bgcolor: accentLight,
              flex: 'none',
              animation: 'ip-dot 1.2s ease-in-out infinite',
            }}
          />
          <Box
            key={msgIndex}
            component='span'
            sx={{ fontSize: s(13), color: GREY_700, animation: 'ip-msg 2.4s ease-in-out' }}
          >
            {rotatingMsg}
          </Box>
        </Box>
      )}

      {/* Reassurance footer */}
      {!hideFooter && footerText && (
        <Typography
          sx={{
            mt: sp(2),
            fontSize: s(11),
            lineHeight: 1.5,
            color: GREY_500,
            textAlign: 'center',
          }}
        >
          {footerText}
        </Typography>
      )}
    </Box>
  )
}
